import { observer } from 'mobx-react-lite'
import { ContentError } from '@/components/ContentError'
import { ContentLoading } from '@/components/ContentLoading'
import { type UserJobsTabViewModel } from '@/features/jobs/list/UserJobsTabViewModel'
import { VirtualTableContainer } from '@/components/VirtualTableContainer'
import { WorkspaceFilterViewHeadingContainer } from '@/components/WorkspaceFilterViewHeadingContainer'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { useNavigate } from 'react-router'
import { links } from '@/utils/link'
import { UserDetailsJobsTableHeaderRowContainer } from '@/features/jobs/list/components/UserJobsTab/UserDetailsJobsTableHeaderRow/UserDetailsJobsTableHeaderRowContainer'
import { JobsTableCellContainer } from '@/features/jobs/list/components/JobManagement/JobsTable/JobsTableCellContainer'
import { JobsViewerHeaderContainer } from '@/features/jobs/list/components/JobManagement/JobsViewerHeader/JobsViewerHeaderContainer'
import { JobCountContainer } from '@/features/jobs/list/components/JobManagement/JobCountContainer'
import { NoResultsEmptyState } from '@/components/NoResultsEmptyState'
import { TableTab } from '@/components/TableTab'
import { useFeatures } from '@/contexts/root-store-context'

export interface UserJobsTabContainerProps {
  vm: UserJobsTabViewModel
}

export const UserJobsTabContainer = observer(function UserJobsTabContainer({
  vm,
}: UserJobsTabContainerProps) {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))
  const navigate = useNavigate()

  return (
    <TableTab
      toolbar={
        <>
          <WorkspaceFilterViewHeadingContainer
            allOptionText="All Workspaces"
            primaryText=""
            filter={vm.workspacesFilter}
            viewHeadingSize="small"
            testId="workspace-filter-view-heading-dropdown"
          />

          <JobCountContainer vm={vm.jobManagementVm} />
        </>
      }
    >
      {vm.jobManagementVm.fetch.match({
        pending: () => <ContentLoading />,
        rejected: (err) => <ContentError error={err} />,
        resolved: () => (
          <>
            <JobsViewerHeaderContainer vm={vm.jobManagementVm} />
            {!vm.shouldShowEmptyState ? (
              <VirtualTableContainer
                overscan={50}
                scroller={vm.jobManagementVm.searcher.scroller}
                data={vm.jobManagementVm.searcher.jobs}
                headerContent={() => (
                  <UserDetailsJobsTableHeaderRowContainer
                    vm={vm.jobManagementVm}
                  />
                )}
                itemContent={(_index, job) => (
                  <JobsTableCellContainer vm={vm.jobManagementVm} job={job} />
                )}
                onPressItemRow={(job: SearchableJob) =>
                  navigate(links.job(job.id))
                }
              />
            ) : (
              <NoResultsEmptyState
                corporateMode={shellInfo.corporateMode}
                isSearchFilterApplied={vm.isSearchFilterApplied}
                onClearSearchFilter={vm.clearFilters}
              />
            )}
          </>
        ),
      })}
    </TableTab>
  )
})
